import { Component } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import produce from "immer";
import { withTranslation } from "react-i18next";
import Dialog from "src/components/Dialog";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import { withApp } from "src/components/App/Context";
import commandFiles from "src/modules/command/command-files";
import View from "./View";
import * as actions from "../../duck/actions";
import * as ajax from "../../ajax/cloud";
import colors from "../../colors";
import icons from "../../commands-icons";
import RecorderStatus from "./RecorderStatusEnum";
import AudioRecorder from "./components/AudioRecorder";
import InputFields from "./components/InputFields";
import CommandPreview from "./components/CommandPreview";

import { v4 as uuid4 } from "uuid";
const log = window.require("electron-log");

class CommandEditor extends Component {
  state = {
    recorderStatus:
      this.props.command && this.props.command.audioFile
        ? RecorderStatus.PAUSED_HAS_RECORDING
        : RecorderStatus.PAUSED_NO_RECORDING,
    requestingPermissions: false,
    recordingStartDate: null,
    submitting: false,
    error: false,
    recordedAudioData: null,
    newCommandUuid: this.props.command ? null : uuid4(),
    command: {
      objectId: this.props.command ? this.props.command.objectId : null,
      name: this.props.command?.name || "",
      color: this.props.command
        ? this.props.command.color
        : Object.values(colors)[0].name,
      icon: this.props.command
        ? this.props.command.icon
        : Object.values(icons)[0].name,
      audioFile: this.props.command ? this.props.command.audioFile : undefined,
      audioDuration: this.props.command
        ? this.props.command.audioDuration
        : undefined
    }
  };
  commandRecording = new Audio();

  componentDidMount() {
    const { command } = this.props;
    if (command && command.audioFile) {
      this.commandRecording.src = commandFiles.getSrc(command.audioFile.name);
    }
    this.commandRecording.addEventListener("timeupdate", this.updateSelf);
    this.commandRecording.addEventListener("durationchange", this.updateSelf);
  }
  componentWillUnmount() {
    this.commandRecording.removeEventListener("timeupdate", this.updateSelf);
    this.commandRecording.removeEventListener(
      "durationchange",
      this.updateSelf
    );
    this.commandRecording.pause();
    if (
      this.mediaRecorder &&
      this.state.recorderStatus === RecorderStatus.RECORDING
    ) {
      this.mediaRecorder.ondataavailable = () => {};
      this.mediaRecorder.stop();
    }
  }
  updateSelf = (e) => {
    this.forceUpdate();
  };

  handleSave = async (e) => {
    const { command, recordedAudioData, newCommandUuid } = this.state;
    let { name, color, icon, audioFile, audioDuration, objectId } = command;
    const { dispatch, afterSave, app } = this.props;
    name = name || undefined;

    this.setState({ submitting: true });

    let newCommand;
    let newAudioFile;
    try {
      if (recordedAudioData) {
        newAudioFile = await ajax.saveAudioFile(recordedAudioData);
        await commandFiles.write(newAudioFile.name, recordedAudioData);
      }
      if (objectId == null) {
        newCommand = await ajax.createCommand({
          uuid: newCommandUuid,
          name,
          icon,
          color,
          audioFile: newAudioFile,
          audioDuration: this.commandRecording.duration * 1000
        });
      } else {
        newCommand = await ajax.updateCommand({
          name,
          icon,
          color,
          commandId: this.state.command.objectId,
          audioFile: newAudioFile ? newAudioFile : audioFile,
          audioDuration: newAudioFile
            ? this.commandRecording.duration * 1000
            : audioDuration
        });
      }
      dispatch(actions.saveCommand(newCommand));
      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.COMMANDS
      );

      if (recordedAudioData && audioFile) commandFiles.delete(audioFile.name);

      afterSave();
    } catch (error) {
      log.error(error);
      this.setState({ error: true, submitting: false });
    }
  };

  handleNameChange = (e) => {
    const { value } = e.target;
    this.setState(
      produce((prevState) => {
        prevState.command.name = value;
      })
    );
  };
  handleIconChange = (iconName) => {
    this.setState(
      produce((prevState) => {
        prevState.command.icon = iconName;
      })
    );
  };
  handleColorChange = (colorName) => {
    this.setState(
      produce((prevState) => {
        prevState.command.color = colorName;
      })
    );
  };

  initializeMediaRecorder() {
    this.mediaRecorder = new window.Recorder({
      encoderPath: "./opus-recorder/dist/encoderWorker.min.js",
      maxFramesPerPage: 5
    });
    this.mediaRecorder.ondataavailable = (arrayBuffer) => {
      const blob = new Blob([arrayBuffer], { type: "audio/ogg" });
      this.commandRecording.src = URL.createObjectURL(blob);
      this.setState({
        recorderStatus: RecorderStatus.PAUSED_HAS_RECORDING,
        recordedAudioData: arrayBuffer
      });
    };
  }

  startRecording = async () => {
    const { recorderStatus } = this.state;
    const { app } = this.props;
    if (recorderStatus === RecorderStatus.PLAYING) {
      this.stopPlaying();
    }
    if (this.mediaRecorder == null) {
      this.setState({ requestingPermissions: true });

      const status = await app.permissions.getMediaAccessStatus("microphone");
      let hasPermission = status === "granted";

      if (status === "not-determined") {
        hasPermission = await app.permissions.resolveMediaPermission(
          "microphone"
        );
      }

      this.setState({
        requestingPermissions: false
      });

      if (!hasPermission) {
        status === "denied" && app.permissions.openDialog("microphone");
        return;
      }

      this.initializeMediaRecorder();
    }
    this.mediaRecorder.start();
    this.setState({
      recorderStatus: RecorderStatus.RECORDING,
      recordingStartDate: Date.now()
    });
  };
  stopRecording = () => {
    this.mediaRecorder.stop();
  };
  toggleRecord = () => {
    const { recorderStatus, requestingPermissions } = this.state;
    if (requestingPermissions) return;
    recorderStatus === RecorderStatus.RECORDING
      ? this.stopRecording()
      : this.startRecording();
  };

  startPlaying = () => {
    this.commandRecording.addEventListener(
      "ended",
      () => {
        this.setState({ recorderStatus: RecorderStatus.PAUSED_HAS_RECORDING });
      },
      { once: true }
    );
    this.commandRecording.currentTime = 0;
    this.commandRecording.play();
    this.setState({ recorderStatus: RecorderStatus.PLAYING });
  };
  stopPlaying = () => {
    this.commandRecording.pause();
    this.setState({ recorderStatus: RecorderStatus.PAUSED_HAS_RECORDING });
  };
  togglePlay = () =>
    this.state.recorderStatus === RecorderStatus.PLAYING
      ? this.stopPlaying()
      : this.startPlaying();

  render() {
    const {
      error,
      submitting,
      command,
      recorderStatus,
      recordingStartDate,
      recordedAudioData
    } = this.state;
    const { t, onClose, onBack } = this.props;
    const hasRecording = Boolean(command.audioFile || recordedAudioData);

    const recorder = (
      <AudioRecorder
        t={t}
        togglePlay={this.togglePlay}
        toggleRecord={this.toggleRecord}
        status={recorderStatus}
        recordingDuration={this.commandRecording.duration * 1000}
        replayCurrentTime={this.commandRecording.currentTime * 1000}
        recordingStartDate={recordingStartDate}
      />
    );
    const inputFields = (
      <InputFields
        t={t}
        command={command}
        onNameChange={this.handleNameChange}
        onIconChange={this.handleIconChange}
        onColorChange={this.handleColorChange}
      />
    );
    const commandPreview = <CommandPreview t={t} command={command} />;

    return (
      <>
        <Dialog
          isOpen={submitting}
          processingText={t("command.editor.saving")}
          isProcessing={true}
        />
        <GenericFailureDialog
          isOpen={error}
          onDismiss={() => this.setState({ error: false })}
        />
        <View
          recorder={recorder}
          inputFields={inputFields}
          commandPreview={commandPreview}
          disableSave={
            recorderStatus === RecorderStatus.RECORDING || !hasRecording
          }
          onClose={onClose}
          onBack={onBack}
          onSave={this.handleSave}
        />
      </>
    );
  }
}

const enhance = compose(withApp, connect(), withTranslation());

export default enhance(CommandEditor);
